import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type { Invoice } from '@freelancer-os/shared-types';
import type { CreateInvoiceInput, UpdateInvoiceInput } from '@freelancer-os/validation';
import { toast } from 'sonner';

export function useInvoices() {
  return useQuery<Invoice[]>({
    queryKey: ['invoices'],
    queryFn: () => fetchApi('/invoices'),
  });
}

export function useInvoice(id: string | null) {
  return useQuery<Invoice>({
    queryKey: ['invoices', id],
    queryFn: () => fetchApi(`/invoices/${id}`),
    enabled: id !== null,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateInvoiceInput) => 
      fetchApi<Invoice>('/invoices', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create invoice');
    }
  });
}

export function useUpdateInvoice(id: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: UpdateInvoiceInput) => 
      fetchApi<Invoice>(`/invoices/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      toast.success('Invoice updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update invoice');
    }
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ deleted: boolean }>(`/invoices/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete invoice');
    }
  });
}

// ── M8-BE Outreach hooks ────────────────────────────────────────────────────

export interface DraftedMessage {
  to: string | null;
  subject: string;
  body: string;
  _fallback?: boolean;
}

export interface SendInvoiceInput {
  to: string;
  subject: string;
  body: string;
}

export function useDraftInvoiceMessage(id: string) {
  return useMutation<DraftedMessage, Error, void>({
    mutationFn: () =>
      fetchApi<DraftedMessage>(`/invoices/${id}/draft-message`, {
        method: 'POST',
      }),
  });
}

export function useSendInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SendInvoiceInput>({
    mutationFn: (input) =>
      fetchApi<void>(`/invoices/${id}/send`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
    },
  });
}

export function useDraftReminder(id: string) {
  return useMutation<DraftedMessage, Error, void>({
    mutationFn: () =>
      fetchApi<DraftedMessage>(`/invoices/${id}/draft-reminder`, {
        method: 'POST',
      }),
  });
}

export function useSendReminder(id: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SendInvoiceInput>({
    mutationFn: (input) =>
      fetchApi<void>(`/invoices/${id}/send-reminder`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
    },
  });
}

// ── Artifact <-> Invoice lookup ─────────────────────────────────────────────

export function useInvoiceByArtifact(threadId: string | null, artifactIndex: number | null) {
  return useQuery<Invoice | null>({
    queryKey: ['invoices', 'by-artifact', threadId, artifactIndex],
    queryFn: () => {
      const params = new URLSearchParams({
        threadId: threadId!,
        artifactIndex: String(artifactIndex),
      });
      return fetchApi<Invoice | null>(`/invoices/by-artifact?${params.toString()}`);
    },
    enabled: threadId !== null && artifactIndex !== null && artifactIndex >= 0,
    staleTime: 15_000,
  });
}

// ── Mark-shared (Zalo / out-of-band) ────────────────────────────────────────

export function useMarkShared(id: string) {
  const queryClient = useQueryClient();
  return useMutation<Invoice, Error, { channel?: 'zalo' | 'sms' | 'other' }>({
    mutationFn: (input) =>
      fetchApi<Invoice>(`/invoices/${id}/mark-shared`, {
        method: 'POST',
        body: JSON.stringify({ channel: input.channel ?? 'zalo' }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices', 'by-artifact'] });
    },
  });
}
